import { Trans, useTranslation } from 'react-i18next';
import { useGetAvailableRefinLoans } from '@/query/queries/loan/useGetAvailableRefinLoans';
import { APP_LINKS } from '@/helpers/links';
import { prettyFloatMoney } from '@/helpers/utils';
import {
  DATE_FORMAT_DD_MMM_YYYY,
  DATE_FORMAT_DD_MMMM,
  DATE_FORMAT_DD_MMMM_YYYY,
} from '@/helpers/common';
import { useRefin } from '@/providers/refin';
import { MyLink } from '@/components/MyLink';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { prettyDate } from '@/helpers/date';
import { REFIN_STATUSES } from '@/helpers/constants';
import Preloader from '@/components/UI/Preloader';
import IconRefresh from './icon_refresh.svg';
import s from './index.module.scss';

export const AvailableRefinLoans = () => {
  const { data, isLoading } = useGetAvailableRefinLoans();
  const { t } = useTranslation('dashboard');
  const { onApply } = useRefin();
  const { isMainFounder } = useGetMe();

  if (isLoading) {
    return (
      <div className={s.wrapper}>
        <Preloader style={{ height: '10vh' }} />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <div className="f-h3 f-500">{t('avail_refin_loans.loans_available_for_refinance')}</div>
        <MyLink href={APP_LINKS.loanTermsRefinancing()} isExternal newWindow>
          {t('avail_refin_loans.learn_more')} →
        </MyLink>
      </div>
      <div className={s.list}>
        {data?.list.map(loan => (
          <MyLink
            href={APP_LINKS.loans + `?fundraising=${loan.fundraising_id}`}
            key={loan.fundraising_name}
            className={s.card}
          >
            <div className={s.card_top}>
              <div className={s.details}>
                <div className="label">{t('avail_refin_loans.fundraising_round')}</div>
                <div className="f-body">{loan.fundraising_name}</div>
              </div>
              <div className={s.details}>
                <div className="label">{t('avail_refin_loans.repayment_amount')}</div>
                <div className="f-body">{prettyFloatMoney(loan.repayment_amount)}</div>
              </div>
              <div className={s.details}>
                <div className="label">{t('avail_refin_loans.repayment_date')}</div>
                <div className="f-body">
                  {prettyDate(loan.repayment_date, DATE_FORMAT_DD_MMMM_YYYY)}
                </div>
              </div>
            </div>

            {loan.refinancing_info.status === REFIN_STATUSES.applied && (
              <div className={s.refin}>
                <div className={s.refin_text}>
                  <img src={IconRefresh} alt="icon" width={24} height={24} />
                  <div>
                    <Trans
                      ns="dashboard"
                      i18nKey="avail_refin_loans.refin_application_accepted"
                      components={{
                        Span: <span className="f-700" />,
                      }}
                      values={{
                        date: prettyDate(
                          loan.refinancing_info.fundraising_start_date,
                          DATE_FORMAT_DD_MMM_YYYY,
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {loan.refinancing_info.status === REFIN_STATUSES.available && (
              <div className={s.refin}>
                {loan.refinancing_info.refin_fee_before ? (
                  <div className={s.refin_text}>
                    <Trans
                      ns="dashboard"
                      i18nKey="avail_refin_loans.refin_fee_until"
                      components={{
                        Span: <span className="f-700" />,
                      }}
                      values={{
                        fee: loan.refinancing_info.refin_fee_before,
                        validUntilDate: prettyDate(
                          loan.refinancing_info.valid_until_date,
                          DATE_FORMAT_DD_MMMM,
                        ),
                      }}
                    />
                  </div>
                ) : (
                  <div className={s.refin_text}>
                    <img src={IconRefresh} alt="icon" width={24} height={24} />
                    <span>
                      <Trans
                        ns="dashboard"
                        i18nKey="avail_refin_loans.refin_apply_late"
                        components={{
                          Span: <span className="f-700" />,
                        }}
                        values={{
                          feeLate: loan.refinancing_info.refin_fee_late,
                          date: prettyDate(
                            loan.refinancing_info.refin_fee_late_date,
                            DATE_FORMAT_DD_MMMM,
                          ),
                        }}
                      />
                    </span>
                  </div>
                )}
                {isMainFounder && (
                  <button
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      onApply({ ...loan, refinancing: loan.refinancing_info });
                    }}
                  >
                    {t('avail_refin_loans.apply_now')}
                  </button>
                )}
              </div>
            )}
          </MyLink>
        ))}
      </div>
    </div>
  );
};
